import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, Sparkles, Pen, BookOpen, Zap, MessageCircle, Target, CheckCircle } from 'lucide-react';

export default function GuidelinesModal({ open, onClose }) {
  const { t } = useTranslation();
  
  const guidelines = [
    {
      icon: Sparkles,
      title: t('guidelines.guideline1Title'),
      description: t('guidelines.guideline1Desc'),
      color: "purple"
    },
    {
      icon: Pen,
      title: t('guidelines.guideline2Title'),
      description: t('guidelines.guideline2Desc'),
      color: "blue",
      action: t('guidelines.guideline2Action')
    },
    {
      icon: BookOpen,
      title: t('guidelines.guideline3Title'),
      description: t('guidelines.guideline3Desc'),
      color: "green",
      action: t('guidelines.guideline3Action')
    },
    {
      icon: Zap,
      title: t('guidelines.guideline4Title'),
      description: t('guidelines.guideline4Desc'),
      color: "yellow",
      action: t('guidelines.guideline4Action')
    },
    {
      icon: MessageCircle,
      title: t('guidelines.guideline5Title'),
      description: t('guidelines.guideline5Desc'),
      color: "pink",
      action: t('guidelines.guideline5Action')
    },
    {
      icon: Target,
      title: t('guidelines.guideline6Title'),
      description: t('guidelines.guideline6Desc'),
      color: "indigo"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-600',
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      pink: 'bg-pink-100 text-pink-600',
      indigo: 'bg-indigo-100 text-indigo-600'
    };
    return colors[color] || colors.purple;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              {t('guidelines.welcomeTitle')}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-gray-700 text-sm leading-relaxed">
              {t('guidelines.intro')}
            </p>
          </div>

          {/* Guidelines */}
          <div className="space-y-4">
            {guidelines.map((guideline, index) => {
              const Icon = guideline.icon;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClasses(guideline.color)}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        {guideline.title}
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          Step {index + 1}
                        </span>
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {guideline.description}
                      </p>
                      {guideline.action && (
                        <p className="text-xs text-gray-500 italic">
                          💡 {guideline.action}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Start Checklist */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {t('guidelines.quickStartTitle')}
            </h3>
            <ul className="space-y-2 text-sm text-green-800">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>{t('guidelines.quickStart1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>{t('guidelines.quickStart2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>{t('guidelines.quickStart3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>{t('guidelines.quickStart4')}</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={onClose}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              size="lg"
            >
              Got It! Let's Start 🚀
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
